import { useEffect, useMemo, useState } from "react";
import { addWeeks } from "date-fns";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useAuth, User } from "../../core/providers/auth";
import { Workout } from "../../providers/workouts";
import { useWorkoutSettings } from "../../providers/workout-settings";
import LogicWorkout from "./logic-workout";

import {
  applyWorkoutFormToWorkout,
  createDayDate,
  createWorkout,
  getInitWorkoutFormModel,
  RecurrenceEditScope,
  updateWorkout,
  WorkoutFormModel,
} from "../../lib/workout-form/transformers";
import { FormInputs } from "../../reducers/workoutReducer";
import {
  collection,
  deleteDoc,
  doc,
  DocumentReference,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from "firebase/firestore";
import { firestore } from "../../core";
import { features } from "../../core/config/features";
import Loading from "../../core/views/loading";

type Props = {};
type CalendarCreateState = {
  initialStart?: string;
  initialEnd?: string;
};

function cloneWorkout(workout: Workout): Workout {
  return {
    ...workout,
    admin: { ...workout.admin },
    gear: [...workout.gear],
    participantsList: [...workout.participantsList],
    waitlist: [...workout.waitlist],
    coaches: [...workout.coaches],
  };
}

function slugify(value: string) {
  return value
    .trim()
    .toLowerCase()
    .replace(/[åä]/g, "a")
    .replace(/ö/g, "o")
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-|-$/g, "");
}

//fetch firebase workout from params, if exists and is admin set as default, otherwise empty form
const FirebaseWorkout = (props: Props) => {
  const { id } = useParams();
  const [edit, setEdit] = useState(false);
  const [loading, setLoading] = useState(true);
  const [initWorkout, setInitWorkout] = useState<Workout>();
  const { user } = useAuth();
  const { equipment, locations } = useWorkoutSettings();
  const navigate = useNavigate();
  const location = useLocation();
  const createState = location.state as CalendarCreateState | null;

  const initialWorkoutForm = useMemo(() => {
    const workoutForm = getInitWorkoutFormModel(initWorkout);

    if (!initWorkout && createState?.initialStart) {
      const start = new Date(createState.initialStart);
      const end = createState.initialEnd
        ? new Date(createState.initialEnd)
        : new Date(start.getTime() + 60 * 60 * 1000);

      workoutForm[FormInputs.date].input = start;
      workoutForm[FormInputs.startTime].input = start;
      workoutForm[FormInputs.endTime].input = end;
    }

    return workoutForm;
  }, [createState?.initialEnd, createState?.initialStart, initWorkout]);

  useEffect(() => {
    const findWorkout = async () => {
      if (id) {
        const workoutReference: DocumentReference = doc(
          firestore,
          "workouts",
          id
        );
        const workoutDoc = await getDoc(workoutReference);
        const workout: Workout = workoutDoc.data() as Workout;
        if (workout && workout.id === id) {
          setEdit(true);
          setInitWorkout(workout);
        } else {
          navigate("/workout");
        }
      }
      setLoading(false);
    };
    findWorkout();
  }, [edit, id, navigate]);

  const saveNewSettingsFromWorkout = async (workout: Workout) => {
    const writes: Promise<void>[] = [];
    const locationName = workout.location.trim();
    const knownLocation = locations.some(
      (location) => location.name.toLowerCase() === locationName.toLowerCase()
    );

    if (locationName && !knownLocation) {
      writes.push(
        setDoc(
          doc(firestore, "locations", slugify(locationName)),
          {
            name: locationName,
            active: true,
            sortOrder: locations.length * 10 + 10,
          },
          { merge: true }
        )
      );
    }

    const knownEquipment = new Set(
      equipment.map((equipment) => equipment.name.toLowerCase())
    );

    workout.gear
      .map((gear) => gear.trim())
      .filter((gear) => gear.length > 0)
      .forEach((gear, index) => {
        if (knownEquipment.has(gear.toLowerCase())) return;

        writes.push(
          setDoc(
            doc(firestore, "equipment", slugify(gear)),
            {
              name: gear,
              active: true,
              sortOrder: (equipment.length + index) * 10 + 10,
            },
            { merge: true }
          )
        );
      });

    await Promise.all(writes);
  };

  const handleSubmit = async (
    workoutForm: WorkoutFormModel,
    recurrenceCount: number,
    scope: RecurrenceEditScope
  ) => {
    let workout: Workout;
    let workoutRef: DocumentReference;
    if (initWorkout) {
      if (
        scope === "following" &&
        initWorkout.recurrenceSeriesId
      ) {
        const seriesSnapshot = await getDocs(
          query(
            collection(firestore, "workouts"),
            where("recurrenceSeriesId", "==", initWorkout.recurrenceSeriesId)
          )
        );
        const currentIndex = initWorkout.recurrenceIndex ?? 0;
        const baseWorkout = updateWorkout(workoutForm, cloneWorkout(initWorkout));
        if (!features.credits) baseWorkout.credits = 0;

        const futureWorkouts = seriesSnapshot.docs
          .map((workoutDoc) => workoutDoc.data() as Workout)
          .filter((seriesWorkout) => {
            const seriesIndex = seriesWorkout.recurrenceIndex;
            if (typeof seriesIndex === "number") {
              return seriesIndex >= currentIndex;
            }

            return seriesWorkout.startTime.toDate() >= initWorkout.startTime.toDate();
          })
          .sort((a, b) => {
            const aIndex = a.recurrenceIndex ?? Number.MAX_SAFE_INTEGER;
            const bIndex = b.recurrenceIndex ?? Number.MAX_SAFE_INTEGER;

            if (aIndex !== bIndex) {
              return aIndex - bIndex;
            }

            return a.startTime.toDate().getTime() - b.startTime.toDate().getTime();
          });

        await saveNewSettingsFromWorkout(baseWorkout);

        await Promise.all(
          futureWorkouts.map((seriesWorkout) => {
            const updatedWorkout = cloneWorkout(seriesWorkout);
            const targetIndex = seriesWorkout.recurrenceIndex ?? currentIndex;
            const weekOffset = Math.max(0, targetIndex - currentIndex);
            const nextStart = addWeeks(baseWorkout.startTime.toDate(), weekOffset);
            const nextEnd = addWeeks(baseWorkout.endTime.toDate(), weekOffset);

            applyWorkoutFormToWorkout(workoutForm, updatedWorkout, {
              preserveParticipantsCount: true,
            });
            if (!features.credits) updatedWorkout.credits = 0;
            updatedWorkout.startTime = Timestamp.fromDate(nextStart);
            updatedWorkout.endTime = Timestamp.fromDate(nextEnd);
            updatedWorkout.date = Timestamp.fromDate(createDayDate(nextStart));
            updatedWorkout.recurrenceSeriesId = initWorkout.recurrenceSeriesId;
            updatedWorkout.recurrenceIndex = targetIndex;
            updatedWorkout.recurrenceCount =
              initWorkout.recurrenceCount ?? updatedWorkout.recurrenceCount;

            return setDoc(
              doc(firestore, "workouts", updatedWorkout.id),
              updatedWorkout,
              { merge: true }
            );
          })
        );

        setInitWorkout(baseWorkout);
        setEdit(true);
        return baseWorkout;
      }

      workout = updateWorkout(workoutForm, initWorkout);
      if (!features.credits) workout.credits = 0;
      workoutRef = doc(firestore, "workouts", workout.id);
      await saveNewSettingsFromWorkout(workout);
      return setDoc(workoutRef, workout, { merge: true }).then(() => {
        setInitWorkout(workout);
        setEdit(true);
        return workout;
      });
    }

    const count = Math.max(1, recurrenceCount);
    let firstWorkout: Workout | null = null;
    const firstWorkoutRef = doc(collection(firestore, "workouts"));
    const recurrenceSeriesId = count > 1 ? firstWorkoutRef.id : undefined;
    firstWorkout = createWorkout(workoutForm, user as User, firstWorkoutRef.id);
    if (!features.credits) firstWorkout.credits = 0;

    await saveNewSettingsFromWorkout(firstWorkout);

    await Promise.all(Array.from({ length: count }, async (_, index) => {
      const recurringWorkoutRef =
        index === 0 ? firstWorkoutRef : doc(collection(firestore, "workouts"));
      const recurringWorkout =
        index === 0
          ? (firstWorkout as Workout)
          : createWorkout(workoutForm, user as User, recurringWorkoutRef.id);

      if (!features.credits) recurringWorkout.credits = 0;
      if (recurrenceSeriesId) {
        recurringWorkout.recurrenceSeriesId = recurrenceSeriesId;
        recurringWorkout.recurrenceIndex = index;
        recurringWorkout.recurrenceCount = count;
      }

      if (index > 0) {
        const recurringStart = addWeeks(recurringWorkout.startTime.toDate(), index);
        const recurringEnd = addWeeks(recurringWorkout.endTime.toDate(), index);

        recurringWorkout.startTime = Timestamp.fromDate(recurringStart);
        recurringWorkout.endTime = Timestamp.fromDate(recurringEnd);
        recurringWorkout.date = Timestamp.fromDate(
          createDayDate(recurringStart)
        );
      }

      return setDoc(recurringWorkoutRef, recurringWorkout, { merge: true });
    }));

    if (!firstWorkout) {
      throw new Error("Kunde inte skapa pass");
    }

    setInitWorkout(firstWorkout);
    setEdit(true);
    return firstWorkout;
  };

  const handleDelete = async (scope: RecurrenceEditScope) => {
    if (
      scope === "following" &&
      initWorkout?.recurrenceSeriesId
    ) {
      const seriesSnapshot = await getDocs(
        query(
          collection(firestore, "workouts"),
          where("recurrenceSeriesId", "==", initWorkout.recurrenceSeriesId)
        )
      );
      const currentIndex = initWorkout.recurrenceIndex ?? 0;
      const workoutsToDelete = seriesSnapshot.docs
        .map((workoutDoc) => workoutDoc.data() as Workout)
        .filter((seriesWorkout) => {
          const seriesIndex = seriesWorkout.recurrenceIndex;
          if (typeof seriesIndex === "number") {
            return seriesIndex >= currentIndex;
          }

          return seriesWorkout.startTime.toDate() >= initWorkout.startTime.toDate();
        });

      await Promise.all(
        workoutsToDelete.map((workout) =>
          deleteDoc(doc(firestore, "workouts", workout.id))
        )
      );
      return;
    }

    const workoutRef = doc(firestore, "workouts", id as string);
    return deleteDoc(workoutRef);
  };
  if (loading) {
    return <Loading></Loading>;
  }
  return (
    <LogicWorkout
      onDelete={handleDelete}
      onSubmit={handleSubmit}
      workoutForm={initialWorkoutForm}
      edit={edit}
      isRecurringSeriesWorkout={Boolean(initWorkout?.recurrenceSeriesId)}></LogicWorkout>
  );
};

export default FirebaseWorkout;
